<template>
    <nav :aria-label="t('navigation.mainNavigation')" class="hidden md:flex flex-col gap-2 grow overflow-y-auto">
        <template v-for="item in defaultPanes" :key="item.id">
            <BaseTooltip v-if="item" :disabled="showLabels" side="right">
                <template #trigger>
                    <component
                        :is="item.url ? BaseLink : 'button'"
                        type="button"
                        :data-testid="`nav-${item.id}`"
                        :href="item.url"
                        :data="item.url ? { chat_source: CHATSOURCE.LEFT_SIDEBAR } : undefined"
                        :preserve-scroll="false"
                        :aria-label="paneAriaLabel(item)"
                        :aria-current="isCurrentPage(item.url) ? 'page' : undefined"
                        :class="menuItemClass({ collapsed: !showLabels })"
                        @click="handlePaneClick(item)">
                        <span class="relative shrink-0 leading-none">
                            <component :is="item.icon" v-if="iconIsComponent(item.icon)" class="nav-icon-legacy" aria-hidden="true" />
                            <i v-else-if="(item.icon as string).startsWith('bi-')" class="bi nav-icon-legacy leading-none" :class="item.icon" aria-hidden="true"></i>
                            <span v-else class="nav-icon-legacy leading-none" aria-hidden="true">{{ item.icon }}</span>
                            <NotificationMarble v-if="!showLabels && panesWithNotifications.includes(item.id)" :size="1" class="absolute -top-0.5 -right-0.5" />
                        </span>
                        <div v-if="showLabels" class="ml-3 flex flex-row items-center text-left min-w-0">
                            {{ item.title }}
                        </div>
                        <NotificationMarble v-if="showLabels && panesWithNotifications.includes(item.id)" :size="2" class="ml-auto" />
                    </component>
                </template>
                <template v-if="!showLabels" #content>{{ item.title }}</template>
            </BaseTooltip>
        </template>
        <h3 v-if="showSectionTitles && (loadingWorkPanes || myWorkPanes.length || dashboardLink)" :class="`${menuSectionTitleClass()} mt-10`">
            {{ t("navigation.legacySections.work") }}
        </h3>
        <UserCalendarSkeleton v-if="loadingWorkPanes" class="w-full h-10" :section-count="1" />
        <template v-for="item in myWorkPanes" v-else :key="item.id">
            <BaseTooltip v-if="item" :disabled="showLabels" side="right">
                <template #trigger>
                    <component
                        :is="item.url ? BaseLink : 'button'"
                        :data-testid="`nav-${item.id}`"
                        :href="item.url"
                        :data="item.url ? { chat_source: CHATSOURCE.LEFT_SIDEBAR } : undefined"
                        :preserve-scroll="false"
                        :aria-label="paneAriaLabel(item)"
                        :aria-current="isCurrentPage(item.url) ? 'page' : undefined"
                        :class="menuItemClass({ collapsed: !showLabels })"
                        @click="handlePaneClick(item)">
                        <span class="relative shrink-0 leading-none">
                            <component :is="item.icon" v-if="iconIsComponent(item.icon)" class="nav-icon-legacy" aria-hidden="true" />
                            <i v-else-if="(item.icon as string).startsWith('bi-')" class="bi nav-icon-legacy leading-none" :class="item.icon" aria-hidden="true"></i>
                            <span v-else class="nav-icon-legacy leading-none" aria-hidden="true">{{ item.icon }}</span>
                            <BaseCountBadge v-if="paneCount(item.id) > 0" :count="paneCount(item.id)" class="absolute -top-1.5 -right-1.5" aria-hidden="true" />
                            <NotificationMarble
                                v-else-if="!showLabels && panesWithNotifications.includes(item.id)"
                                :size="1"
                                class="absolute -top-0.5 -right-0.5"
                                aria-hidden="true" />
                        </span>
                        <div v-if="showLabels" class="ml-3 flex flex-row items-center text-left min-w-0 mr-2">
                            {{ item.title }}
                        </div>
                        <NotificationMarble
                            v-if="showLabels && paneCount(item.id) === 0 && panesWithNotifications.includes(item.id)"
                            :size="2"
                            class="ml-auto"
                            aria-hidden="true" />
                    </component>
                </template>
                <template v-if="!showLabels" #content>{{ item.title }}</template>
            </BaseTooltip>
        </template>
        <BaseTooltip v-if="dashboardLink" :disabled="showLabels" side="right">
            <template #trigger>
                <BaseLink
                    :href="dashboardLink.url"
                    :aria-label="dashboardLink.title"
                    :aria-current="isCurrentPage(dashboardLink.url) ? 'page' : undefined"
                    :class="menuItemClass({ textLeft: true, collapsed: !showLabels })">
                    <i class="bi nav-icon-legacy leading-none shrink-0" :class="dashboardLink.icon" aria-hidden="true"></i>
                    <div v-if="showLabels" class="ml-3 min-w-0">{{ dashboardLink.title }}</div>
                </BaseLink>
            </template>
            <template v-if="!showLabels" #content>{{ dashboardLink.title }}</template>
        </BaseTooltip>
        <h3 v-if="showSectionTitles && (staffLinks.length || instanceSwitcher)" :class="`${menuSectionTitleClass()} mt-5`">{{ t("navigation.legacySections.admin") }}</h3>
        <template v-for="link in staffLinks" :key="link.url">
            <BaseTooltip :disabled="showLabels" side="right">
                <template #trigger>
                    <component
                        :is="link.is_inertia ? BaseLink : 'a'"
                        :href="link.url"
                        :method="link.method || 'GET'"
                        :aria-label="link.title"
                        :aria-current="isCurrentPage(link.url) ? 'page' : undefined"
                        :class="menuItemClass({ textLeft: true, collapsed: !showLabels })">
                        <i class="bi nav-icon-legacy leading-none shrink-0" :class="link.icon" aria-hidden="true"></i>
                        <div v-if="showLabels" class="ml-3 min-w-0">{{ link.title }}</div>
                    </component>
                </template>
                <template v-if="!showLabels" #content>{{ link.title }}</template>
            </BaseTooltip>
        </template>
        <ValencerInstanceSwitcher v-if="instanceSwitcher" :trigger-class="menuItemClass({ textLeft: true, collapsed: !showLabels })" :show-labels="showLabels" legacy />
    </nav>
</template>

<script lang="ts">
type PaneSection = "default" | "work";

interface PaneData {
    id: MainMenuPane;
    title: string;
    // English, for Amplitude: event properties must not change with the user's language.
    analyticsName: string;
    icon: string | Component;
    url?: string;
    component?: Component;
    section: PaneSection;
    order: number;
}

export interface Props {
    showLabels?: boolean;
    showSectionTitles: boolean;
    enabledPanes?: MainMenuPane[];
    panesWithNotifications?: MainMenuPane[];
    paneNotificationCounts?: PaneNotificationCounts;
    loadingWorkPanes?: boolean;
}
</script>

<script setup lang="ts">
import { usePage } from "@inertiajs/vue3";
import type { DashboardLink, OrganizationDetails, StaffLink, UserDetails } from "~vue/appTypes";
import BaseCountBadge from "~vue/components/BaseCountBadge.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import NotificationMarble from "~vue/components/NotificationMarble.vue";
import PurplePlusCircle from "~vue/icons/PurplePlusCircle.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type Component, computed, inject, type Ref } from "vue";
import { useI18n } from "vue-i18n";

import UserCalendarSkeleton from "../../CalendarIntegration/UserCalendarSkeleton.vue";
import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS, MainMenuPane, paneNotificationAriaLabel, type PaneNotificationCounts } from "../menuPanes";
import ValencerInstanceSwitcher from "../ValencerInstanceSwitcher.vue";

const { t } = useI18n();

type MainMenuPageProps = {
    staff_links: StaffLink[];
    dashboard_link: DashboardLink | null;
};

const organizationDetails = inject<Ref<OrganizationDetails | null>>("organization_details");
const userDetails = inject<Ref<UserDetails | null>>("user_details");
const page = usePage<MainMenuPageProps>();
const staffLinks = computed<StaffLink[]>(() => page.props.staff_links ?? []);
// Served by the reverse proxy outside Inertia, so these are plain links, not router navigations.
const dashboardLink = computed<DashboardLink | null>(() => page.props.dashboard_link ?? null);
const instanceSwitcher = computed(() => userDetails?.value?.valencer_instance_switcher ?? null);

const emit = defineEmits(["openPane"]);

const props = withDefaults(defineProps<Props>(), {
    showLabels: true,
    enabledPanes: () => [],
    panesWithNotifications: () => [],
    paneNotificationCounts: () => ({}),
    loadingWorkPanes: false,
});

function paneCount(id: MainMenuPane): number {
    return props.paneNotificationCounts[id] ?? 0;
}

function paneAriaLabel(item: PaneData): string {
    if (item.id === MainMenuPane.GUIDED_SETUP && props.panesWithNotifications.includes(item.id)) {
        return t("navigation.a11y.paneNew", { title: item.title });
    }
    return paneNotificationAriaLabel(item.id, item.title, paneCount(item.id));
}

type PaneDefinition = Omit<PaneData, "title"> & { title: () => string };

const PANE_DATA: Record<MainMenuPane, PaneDefinition> = {
    [MainMenuPane.NEW_CHAT]: {
        id: MainMenuPane.NEW_CHAT,
        analyticsName: "New Chat",
        title: () => t("navigation.legacyPanes.newChat"),
        icon: PurplePlusCircle,
        url: "/coach/start/",
        section: "default",
        order: 1,
    },
    [MainMenuPane.HOME]: {
        id: MainMenuPane.HOME,
        analyticsName: "Home",
        title: () => t("navigation.panes.home"),
        icon: "bi-house",
        url: "/",
        section: "default",
        order: 2,
    },
    [MainMenuPane.HISTORY]: {
        id: MainMenuPane.HISTORY,
        analyticsName: "Chat History",
        title: () => t("navigation.legacyPanes.chatHistory"),
        icon: "bi-chat",
        url: "/past-conversations",
        section: "default",
        order: 3,
    },
    [MainMenuPane.GUIDED_SETUP]: {
        id: MainMenuPane.GUIDED_SETUP,
        analyticsName: "Coaching setup",
        title: () => t("navigation.panes.coachingSetup"),
        icon: "bi-stars",
        url: "/onboarding/calendar-first/setup/",
        section: "default",
        order: 2.5,
    },
    [MainMenuPane.PROFILE]: {
        id: MainMenuPane.PROFILE,
        analyticsName: "Private Profile",
        title: () => t("navigation.legacyPanes.privateProfile"),
        icon: "bi-person-vcard",
        url: "/accounts/user-profile",
        section: "work",
        order: 4,
    },
    [MainMenuPane.COLLABORATION_PROFILE]: {
        id: MainMenuPane.COLLABORATION_PROFILE,
        analyticsName: "Collaboration Profile",
        title: () => t("navigation.legacyPanes.collaborationProfile"),
        icon: COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS,
        url: "/accounts/collaboration-profile",
        section: "work",
        order: 5,
    },
    [MainMenuPane.JOURNEYS]: {
        id: MainMenuPane.JOURNEYS,
        analyticsName: "Journeys",
        title: () => t("navigation.panes.journeys"),
        icon: "bi-compass",
        url: "/journeys/",
        section: "work",
        order: 6,
    },
    [MainMenuPane.DEVELOPMENT_JOURNEYS]: {
        id: MainMenuPane.DEVELOPMENT_JOURNEYS,
        analyticsName: "Development Journeys",
        title: () => t("navigation.legacyPanes.developmentJourneys"),
        icon: "bi-signpost-split",
        url: "/development-journeys/",
        section: "work",
        order: 6.5,
    },
    [MainMenuPane.MY_DEVELOPMENT]: {
        id: MainMenuPane.MY_DEVELOPMENT,
        analyticsName: "My Development",
        title: () => t("myDevelopment.title"),
        icon: "bi-signpost-split",
        url: "/my-development/",
        section: "work",
        order: 6.6,
    },
    [MainMenuPane.CONNECTIONS]: {
        id: MainMenuPane.CONNECTIONS,
        analyticsName: "People",
        title: () => t("navigation.panes.people"),
        icon: "bi-people",
        url: "/people/",
        section: "work",
        order: 7,
    },
    [MainMenuPane.PERFORMANCE_MANAGEMENT]: {
        id: MainMenuPane.PERFORMANCE_MANAGEMENT,
        analyticsName: "Performance Management",
        title: () => t("navigation.legacyPanes.performanceManagement"),
        icon: "bi-graph-up",
        url: "/performance_management",
        section: "work",
        order: 8,
    },
    [MainMenuPane.MY_WORK]: {
        id: MainMenuPane.MY_WORK,
        analyticsName: "My Work",
        title: () => t("navigation.legacyPanes.myWork"),
        icon: "bi-journal-bookmark",
        url: "/intelligence/my-work/",
        section: "work",
        order: 9,
    },
    [MainMenuPane.COMPETENCY_ASSESSMENT]: {
        id: MainMenuPane.COMPETENCY_ASSESSMENT,
        analyticsName: "Competency Assessment",
        title: () => t("navigation.legacyPanes.competencyAssessment"),
        icon: "bi-patch-check",
        url: "/competency-assessment/",
        section: "work",
        order: 11,
    },
    [MainMenuPane.SCHNEIDER_ASSESSMENT]: {
        id: MainMenuPane.SCHNEIDER_ASSESSMENT,
        analyticsName: "IMPACT Leader Code Self-Reflection",
        title: () => t("navigation.panes.impactLeaderCode"),
        icon: "bi-patch-check",
        url: "/schneider-assessment/",
        section: "work",
        order: 12,
    },
    [MainMenuPane.REFLECTIONS]: {
        id: MainMenuPane.REFLECTIONS,
        analyticsName: "Reflections",
        title: () => t("navigation.panes.reflections"),
        icon: "bi-journal-text",
        url: "/reflections",
        section: "work",
        order: 13,
    },
};

const collaborationProfileEnabled = computed(() => props.enabledPanes.includes(MainMenuPane.COLLABORATION_PROFILE));

const panes = computed(() => {
    return props.enabledPanes
        .map((p) => {
            const pane: PaneData = { ...PANE_DATA[p], title: PANE_DATA[p].title() };
            if (p === MainMenuPane.PERFORMANCE_MANAGEMENT) {
                const customLabel = organizationDetails?.value?.perf_review_custom_copy?.page_tab_label;
                if (customLabel) {
                    pane.title = customLabel;
                    pane.analyticsName = customLabel;
                }
            }
            if (p === MainMenuPane.PROFILE && !collaborationProfileEnabled.value) {
                pane.title = t("navigation.panes.profile");
                pane.analyticsName = "Profile";
                pane.icon = "bi-person-circle";
            }
            return pane;
        })
        .sort((a, b) => a.order - b.order);
});

const defaultPanes = computed(() => panes.value.filter((p) => p.section === "default"));
const myWorkPanes = computed(() => panes.value.filter((p) => p.section === "work"));

function isCurrentPage(url?: string): boolean {
    if (!url) {
        return false;
    }

    const currentPath = page.url.split("?")[0].replace(/\/$/, "") || "/";
    const targetPath = url.split("?")[0].replace(/\/$/, "") || "/";
    return targetPath === "/" ? currentPath === "/" : currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

const handlePaneClick = (item: PaneData) => {
    const eventData: Record<string, string | undefined> = {
        id: item.id,
        name: item.analyticsName,
        url: item.url,
    };

    // Only add icon if it's a string (not a Component)
    if (typeof item.icon === "string") {
        eventData.icon = item.icon;
    }

    logUserInteraction(AmplitudeEventNames.MAIN_MENU_ITEM_CLICKED, eventData);

    if (item.id === MainMenuPane.PERFORMANCE_MANAGEMENT) {
        logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_ENTRY_CLICKED, { entry_source: "side_nav" });
    }

    if (!item.url) {
        emit("openPane", item);
    }
};

function menuItemClass(opts?: { textLeft?: boolean; collapsed?: boolean }): string {
    const widthClass = opts?.collapsed ? "w-full" : "nav-item-legacy-bleed";
    const base = `menu-item nav-item-legacy ${widthClass} text-nav-item-text flex flex-row items-center -outline-offset-1`;
    return opts?.textLeft ? `${base} text-left` : base;
}

function menuSectionTitleClass(): string {
    // FIXME: 13px font-bold sans-serif is a one-off with no exact utility match in
    // tailwind-typography.css. Surface to design (Dayna) for a new variant, or
    // accept this as a documented one-off menu-section-title style.
    return "font-sans font-bold text-nav-section-title text-[13px]";
}

function iconIsComponent(icon: string | Component): boolean {
    return typeof icon === "object" && icon !== null;
}
</script>

<!-- .menu-item interaction styles live in tailwind.css so rows rendered by
     other components (e.g. ValencerInstanceSwitcher's trigger) get them too. -->
